import { FC, useState, useEffect } from 'react'
import Button from 'src/components/Button'
import { CampaignComparisonChart } from 'src/components/CampaignComparisonChart'
import { CampaignComparisonModal } from 'src/components/CampaignComparisonModal'
import { SectionTitle } from 'src/components/SectionTitle'
import { useCompareCampaignsQuery } from 'src/reactQuery/useCompareCampaignsQuery'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import {
  clearCampaignComparison,
  getCampaignComparison,
  saveCampaignComparison,
} from 'src/localStorage/campaignComparison'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { ReactComponent as ArrowUpIcon } from 'src/assets/svg/ri-arrow-up-circle-fill.svg'
import { ReactComponent as ScalesIcon } from 'src/assets/svg/ri-scales-3-fill.svg'
import styles from './campaignComparisonSection.module.scss'

interface CampaignComparisonSectionProps {
  endDate?: string
  isLoading?: boolean
  startDate?: string
}

interface SelectedCampaign {
  id: string
  name: string
}

export const CampaignComparisonSection: FC<CampaignComparisonSectionProps> = ({
  isLoading,
  startDate,
  endDate,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedCampaigns, setSelectedCampaigns] = useState<
    SelectedCampaign[]
  >([])
  const [benchmarkCampaign, setBenchmarkCampaign] = useState<string | null>(
    null
  )
  const [showComparison, setShowComparison] = useState(false)
  const [comparisonData, setComparisonData] = useState<any>(null)

  const { mutate: compareCampaigns, isPending: isComparing } =
    useCompareCampaignsQuery()
  const { data: userFeatures } = useGetUserFeaturesQuery()

  // Load saved comparison on mount - first from backend, then fallback to localStorage
  useEffect(() => {
    // Try backend preferences first
    const backendPreferences = userFeatures?.data?.campaignComparisonPreferences
    if (
      backendPreferences &&
      backendPreferences.campaignIds &&
      backendPreferences.campaignIds.length > 0
    ) {
      // Use backend preferences - we need to fetch campaign names
      // For now, we'll use the IDs and the comparison will fetch the names
      // Limit to max 9 campaigns
      const campaignIds = backendPreferences.campaignIds.slice(0, 9)
      const campaigns = campaignIds.map((id) => ({
        id,
        name: '', // Name will be populated from the comparison response
      }))
      setSelectedCampaigns(campaigns)
      setBenchmarkCampaign(backendPreferences.benchmarkCampaignId || null)

      // Auto-fetch comparison data. The picker's "+04:00" strings go through
      // as-is: re-encoding them as UTC ("...Z") cut the window on different
      // day boundaries than every other widget on the page.
      const fromDate = startDate || undefined
      const toDate = endDate || undefined
      setShowComparison(true) // Show chart skeleton immediately

      compareCampaigns(
        {
          benchmarkCampaignId:
            backendPreferences.benchmarkCampaignId || undefined,
          campaignIds: campaignIds,
          fromDate,
          metrics: (backendPreferences.metrics as (
            | 'mentions'
            | 'sources'
            | 'sentiments'
          )[]) || ['mentions', 'sources', 'sentiments'],
          toDate,
        },
        {
          onError: (error) => {
            console.error('Failed to load saved comparison:', error)
            setShowComparison(false)
          },
          onSuccess: (data) => {
            // Update campaign names from response and limit to 9
            const campaignsWithNames = data.data.campaigns
              .slice(0, 9)
              .map((c) => ({
                id: c.id || '',
                name: c.name || '',
              }))
            setSelectedCampaigns(campaignsWithNames)
            setComparisonData(data.data)
          },
        }
      )
    } else {
      // Fallback to localStorage
      const savedComparison = getCampaignComparison()
      if (savedComparison && savedComparison.selectedCampaigns.length > 0) {
        // Limit to max 9 campaigns
        const limitedCampaigns = savedComparison.selectedCampaigns.slice(0, 9)
        setSelectedCampaigns(limitedCampaigns)
        setBenchmarkCampaign(savedComparison.benchmarkCampaign)

        // Auto-fetch comparison data
        const fromDate = startDate || undefined
        const toDate = endDate || undefined

        setShowComparison(true) // Show chart skeleton immediately

        compareCampaigns(
          {
            benchmarkCampaignId: savedComparison.benchmarkCampaign || undefined,
            campaignIds: limitedCampaigns.map((c) => c.id),
            fromDate,
            metrics: ['mentions', 'sources', 'sentiments'],
            toDate,
          },
          {
            onError: (error) => {
              console.error('Failed to load saved comparison:', error)
              // Clear invalid saved data
              clearCampaignComparison()
              setShowComparison(false)
            },
            onSuccess: (data) => {
              setComparisonData(data.data)
            },
          }
        )
      } else if (userFeatures) {
        // No saved preferences at all - default to ALL campaigns
        // Only auto-load if we have user features (to avoid multiple calls)
        // Auto-fetch comparison data for ALL campaigns
        const fromDate = startDate || undefined
        const toDate = endDate || undefined

        setShowComparison(true) // Show chart skeleton immediately

        compareCampaigns(
          {
            campaignIds: [], // Empty array = all campaigns
            fromDate,
            metrics: ['mentions', 'sources', 'sentiments'],
            toDate,
          },
          {
            onError: (error) => {
              console.error('Failed to load all campaigns comparison:', error)
              setShowComparison(false)
            },
            onSuccess: (data) => {
              // Update campaign names from response and limit to 9
              const campaignsWithNames = data.data.campaigns
                .slice(0, 9)
                .map((c) => ({
                  id: c.id || '',
                  name: c.name || '',
                }))
              setSelectedCampaigns(campaignsWithNames)
              setComparisonData(data.data)
            },
          }
        )
      }
    }
  }, [userFeatures, startDate, endDate, compareCampaigns])

  // Re-fetch comparison data when dates change (only for already shown comparisons)
  useEffect(() => {
    if (showComparison && selectedCampaigns.length > 0 && userFeatures) {
      // Format dates to match API expectations (ISO string with UTC timezone)
      const fromDate = startDate || undefined
      const toDate = endDate || undefined

      // Re-fetch comparison with new dates
      compareCampaigns(
        {
          benchmarkCampaignId: benchmarkCampaign || undefined,
          campaignIds: selectedCampaigns.map((c) => c.id),
          fromDate,
          metrics: ['mentions', 'sources', 'sentiments'],
          toDate,
        },
        {
          onError: (error) => {
            console.error('Failed to update campaign comparison:', error)
          },
          onSuccess: (data) => {
            setComparisonData(data.data)
          },
        }
      )
    }
  }, [startDate, endDate])

  const handleCompareClick = () => {
    if (showComparison) {
      setIsModalOpen(true)
    } else {
      setIsModalOpen(true)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleCompare = (
    campaigns: SelectedCampaign[],
    benchmark: string | null
  ) => {
    setSelectedCampaigns(campaigns)
    setBenchmarkCampaign(benchmark)
    setIsModalOpen(false)

    // Save to localStorage as fallback (backend will auto-save)
    saveCampaignComparison({
      benchmarkCampaign: benchmark,
      selectedCampaigns: campaigns,
    })

    // Format dates to match API expectations (ISO string with UTC timezone)
    const fromDate = startDate || undefined
    const toDate = endDate || undefined

    // Call the API
    compareCampaigns(
      {
        benchmarkCampaignId: benchmark || undefined,
        campaignIds: campaigns.map((c) => c.id),
        fromDate,
        metrics: ['mentions', 'sources', 'sentiments'],
        toDate,
      },
      {
        onError: (error) => {
          console.error('Failed to compare campaigns:', error)
          // Still show comparison with fallback data
          setShowComparison(true)
        },
        onSuccess: (data) => {
          setComparisonData(data.data)
          setShowComparison(true)
          // Backend auto-saves preferences, clear localStorage to avoid conflicts
          if (data.data.savedPreferences) {
            clearCampaignComparison()
          }
        },
      }
    )
  }

  const handleChangeComparison = () => {
    setIsModalOpen(true)
  }

  const handleClearComparison = () => {
    clearCampaignComparison()
    setSelectedCampaigns([])
    setBenchmarkCampaign(null)
    setShowComparison(false)
    setComparisonData(null)
  }

  return (
    <>
      {showComparison ? (
        <CampaignComparisonChart
          benchmarkCampaign={benchmarkCampaign}
          comparisonData={comparisonData}
          isLoading={isComparing}
          onChangeComparison={handleChangeComparison}
          onClearComparison={handleClearComparison}
          selectedCampaigns={selectedCampaigns}
        />
      ) : (
        <div className={styles.container}>
          <div className={styles.header}>
            <SectionTitle
              isLoading={isLoading}
              title="Campaigns Comparison"
              tooltipContent="Compare campaign performance metrics"
            />
            <Button background="malibu" onClick={handleCompareClick}>
              <ScalesIcon className={styles.buttonIcon} />
              Compare Campaigns
            </Button>
          </div>

          <div className={styles.tabsContainer}>
            <button className={`${styles.tab} ${styles.active}`}>
              Campaigns
            </button>
            <button className={styles.tab}>Sources</button>
            <button className={styles.tab}>Sentiments</button>
          </div>

          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <AnnouncementIcon />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statLabel}>Total Mentions</div>
                <div className={styles.statValue}>-</div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <ArrowUpIcon />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statLabel}>Most Mentions</div>
                <div className={styles.statValue}>-</div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <ArrowUpIcon style={{ transform: 'rotate(180deg)' }} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statLabel}>Least Mentions</div>
                <div className={styles.statValue}>-</div>
              </div>
            </div>
          </div>

          <div className={styles.emptyState}>
            <ScalesIcon className={styles.emptyIcon} />
            <div className={styles.emptyContent}>
              <h3 className={styles.emptyTitle}>No comparison yet</h3>
              <p className={styles.emptyDescription}>
                Select at least two campaigns to compare their performance
                side-by-side. This will help you uncover trends, spot
                differences, and make data-driven decisions.
              </p>
            </div>
          </div>
        </div>
      )}

      <CampaignComparisonModal
        benchmarkCampaign={benchmarkCampaign}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCompare={handleCompare}
        selectedCampaigns={selectedCampaigns}
      />
    </>
  )
}
